import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useDataProvider, useGetIdentity } from "ra-core";
import { Copy, StickyNote } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  CHECKLIST_ITEMS,
  DIAGNOSTIC_QUESTIONS,
  MODE_LABELS,
  calculerCout,
  formaterNombre,
  modeParDefaut,
  normaliserDiagnostic,
  phraseCout,
  texteCompteRendu,
  type ChecklistId,
  type Diagnostic,
  type DiagnosticMode,
  type Question,
  type QuestionId,
} from "@/fjdigital/diagnostic";
import type { Deal } from "../types";

type Statut = "repos" | "attente" | "enregistrement" | "enregistre" | "erreur";

const MESSAGES_STATUT: Record<Statut, string> = {
  repos: "",
  attente: "Modifications en attente",
  enregistrement: "Enregistrement…",
  enregistre: "Enregistré",
  erreur: "Erreur d'enregistrement, nouvel essai à la prochaine saisie",
};

const nombreDe = (valeur: string): number | null => {
  if (valeur === "") return null;
  const n = Number(valeur.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : null;
};

type Props = {
  deal: Deal;
  /** Secteur de l'entreprise du contact : « independant » présélectionne la variante indépendant. */
  secteur?: string | null;
  contexte: { contact?: string; entreprise?: string; formation?: string };
  /** Délai entre la dernière frappe et l'enregistrement. */
  delaiEnregistrementMs?: number;
};

export const DealDiagnostic = ({
  deal,
  secteur,
  contexte,
  delaiEnregistrementMs = 1000,
}: Props) => {
  const dataProvider = useDataProvider();
  const prefixe = useId();
  const [d, setD] = useState<Diagnostic>(() =>
    normaliserDiagnostic(deal.diagnostic, modeParDefaut(secteur)),
  );
  const [statut, setStatut] = useState<Statut>("repos");
  const [copie, setCopie] = useState<"" | "ok" | "echec">("");
  const [noteStatut, setNoteStatut] = useState<
    "" | "envoi" | "cree" | "maj" | "echec"
  >("");
  const { identity } = useGetIdentity();

  // La référence tient la version la plus récente : l'enregistrement différé ne lit jamais un état périmé.
  const courant = useRef(d);
  const aEnregistrer = useRef(false);
  const minuteur = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const enregistrer = useCallback(async () => {
    if (!aEnregistrer.current) return;
    aEnregistrer.current = false;
    setStatut("enregistrement");
    try {
      await dataProvider.update("deals", {
        id: deal.id,
        data: { diagnostic: courant.current },
        previousData: deal,
      });
      setStatut("enregistre");
    } catch {
      aEnregistrer.current = true;
      setStatut("erreur");
    }
  }, [dataProvider, deal]);
  const enregistrerRef = useRef(enregistrer);
  enregistrerRef.current = enregistrer;

  // Quitter l'onglet dans la seconde qui suit une frappe ne doit rien perdre.
  useEffect(
    () => () => {
      clearTimeout(minuteur.current);
      if (aEnregistrer.current) void enregistrerRef.current();
    },
    [],
  );

  const modifier = (changer: (precedent: Diagnostic) => Diagnostic) => {
    const suivant = changer(courant.current);
    courant.current = suivant;
    setD(suivant);
    aEnregistrer.current = true;
    setStatut("attente");
    setNoteStatut("");
    clearTimeout(minuteur.current);
    minuteur.current = setTimeout(
      () => void enregistrerRef.current(),
      delaiEnregistrementMs,
    );
  };

  const changerMode = (mode: DiagnosticMode) =>
    modifier((p) => ({
      ...p,
      mode,
      calcul: {
        ...p.calcul,
        personnes:
          mode === "ind" && !p.calcul.personnes ? 1 : p.calcul.personnes,
      },
    }));
  const changerReponse = (id: QuestionId, valeur: string) =>
    modifier((p) => ({ ...p, reponses: { ...p.reponses, [id]: valeur } }));
  const changerCalcul = (cle: keyof Diagnostic["calcul"], valeur: string) =>
    modifier((p) => ({
      ...p,
      calcul: { ...p.calcul, [cle]: nombreDe(valeur) },
    }));
  const changerImportance = (n: number) =>
    modifier((p) => ({ ...p, importance: p.importance === n ? null : n }));
  const cocher = (id: ChecklistId, valeur: boolean) =>
    modifier((p) => ({ ...p, checklist: { ...p.checklist, [id]: valeur } }));

  // Writes the report into a note of the deal. The note id is kept on the diagnostic, so
  // a second click refreshes that same note instead of piling up duplicates.
  const deposerNote = async () => {
    if (!identity) return;
    setNoteStatut("envoi");
    const text = texteCompteRendu(courant.current, contexte);
    const date = new Date().toISOString();
    try {
      const existante = courant.current.note_id;
      let misAJour = false;
      if (existante !== null) {
        try {
          await dataProvider.update("deal_notes", {
            id: existante,
            data: { text, date },
            previousData: { id: existante },
          });
          misAJour = true;
        } catch {
          // The note may have been deleted since: recreate it only if it is really gone.
          const toujoursLa = await dataProvider
            .getOne("deal_notes", { id: existante })
            .then(
              () => true,
              () => false,
            );
          if (toujoursLa) throw new Error("deal note update failed");
        }
      }
      if (!misAJour) {
        const { data } = await dataProvider.create("deal_notes", {
          data: { deal_id: deal.id, text, date, sales_id: identity.id },
        });
        modifier((p) => ({ ...p, note_id: Number(data.id) }));
      }
      setNoteStatut(misAJour ? "maj" : "cree");
    } catch {
      setNoteStatut("echec");
    }
  };

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(texteCompteRendu(d, contexte));
      setCopie("ok");
    } catch {
      setCopie("echec");
    }
  };

  const cout = calculerCout(d.calcul);
  const questions = DIAGNOSTIC_QUESTIONS[d.mode];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          className="inline-flex overflow-hidden rounded-md border"
          role="group"
          aria-label="Type de prospect"
        >
          {(["ent", "ind"] as DiagnosticMode[]).map((mode) => (
            <Button
              key={mode}
              type="button"
              size="sm"
              variant={d.mode === mode ? "default" : "ghost"}
              className="rounded-none"
              aria-pressed={d.mode === mode}
              onClick={() => changerMode(mode)}
            >
              {MODE_LABELS[mode]}
            </Button>
          ))}
        </div>
        <span
          role="status"
          className={`text-sm ${statut === "erreur" ? "text-destructive" : "text-muted-foreground"}`}
        >
          {MESSAGES_STATUT[statut]}
        </span>
      </div>

      {questions.slice(0, 5).map((q, index) => (
        <BlocQuestion key={q.id} q={q} index={index}>
          <Textarea
            aria-label={`Réponse : ${q.etape}`}
            rows={3}
            value={d.reponses[q.id]}
            onChange={(e) => changerReponse(q.id, e.target.value)}
          />
          {q.id === "q3" && (
            <div className="mt-1 flex flex-col gap-3 rounded-md bg-muted p-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <ChampNombre
                  id={`${prefixe}-h`}
                  label="Heures par semaine"
                  valeur={d.calcul.heures}
                  onChange={(v) => changerCalcul("heures", v)}
                />
                <ChampNombre
                  id={`${prefixe}-p`}
                  label="Personnes"
                  valeur={d.calcul.personnes}
                  onChange={(v) => changerCalcul("personnes", v)}
                />
                <ChampNombre
                  id={`${prefixe}-t`}
                  label={
                    d.mode === "ind"
                      ? "Son tarif, euros par heure"
                      : "Euros par heure"
                  }
                  valeur={d.calcul.tarif}
                  onChange={(v) => changerCalcul("tarif", v)}
                />
                <ChampNombre
                  id={`${prefixe}-s`}
                  label="Semaines par an"
                  valeur={d.calcul.semaines}
                  onChange={(v) => changerCalcul("semaines", v)}
                />
              </div>
              <div aria-live="polite" className="text-sm">
                {cout ? (
                  <>
                    <p className="font-semibold">{phraseCout(cout)}</p>
                    {cout.euroMois !== null && cout.euroSemaine !== null && (
                      <p className="text-muted-foreground">
                        Chaque mois d'attente : environ{" "}
                        {formaterNombre(cout.euroMois)} euros. Chaque semaine :
                        environ {formaterNombre(cout.euroSemaine)} euros.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground">
                    Saisis les heures et les personnes.
                  </p>
                )}
              </div>
            </div>
          )}
        </BlocQuestion>
      ))}

      <BlocQuestion q={questions[5]} index={5}>
        <div
          className="flex flex-wrap gap-1.5"
          role="group"
          aria-label="Importance de 1 à 10"
        >
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
            <Button
              key={n}
              type="button"
              size="sm"
              variant={d.importance === n ? "default" : "outline"}
              className="w-9"
              aria-label={`Importance ${n} sur 10`}
              aria-pressed={d.importance === n}
              onClick={() => changerImportance(n)}
            >
              {n}
            </Button>
          ))}
        </div>
        <Textarea
          aria-label="Frein exprimé"
          placeholder="Son frein, mot pour mot : qu'est-ce qui l'empêche de mettre un point de plus ?"
          rows={3}
          value={d.reponses.q6}
          onChange={(e) => changerReponse("q6", e.target.value)}
        />
      </BlocQuestion>

      <section className="flex flex-col gap-2">
        <Label htmlFor={`${prefixe}-levier`}>Levier d'urgence utilisé</Label>
        <Textarea
          id={`${prefixe}-levier`}
          rows={2}
          placeholder="Coût de l'attente, comparaison avec les confrères, prix partagé…"
          value={d.levier}
          onChange={(e) => modifier((p) => ({ ...p, levier: e.target.value }))}
        />
      </section>

      <section className="flex flex-col gap-3">
        <h4 className="text-base font-semibold">Clôture</h4>
        <ul className="flex flex-col gap-2">
          {CHECKLIST_ITEMS[d.mode].map((item) => (
            <li key={item.id} className="flex items-start gap-2">
              <Checkbox
                id={`${prefixe}-${item.id}`}
                className="mt-0.5"
                checked={d.checklist[item.id]}
                onCheckedChange={(v) => cocher(item.id, v === true)}
              />
              <Label
                htmlFor={`${prefixe}-${item.id}`}
                className="font-normal leading-snug"
              >
                {item.label}
              </Label>
            </li>
          ))}
        </ul>
        <Label htmlFor={`${prefixe}-suite`}>Prochaine étape</Label>
        <Textarea
          id={`${prefixe}-suite`}
          rows={2}
          value={d.prochaine_etape}
          onChange={(e) =>
            modifier((p) => ({ ...p, prochaine_etape: e.target.value }))
          }
        />
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={deposerNote}
          disabled={!identity || noteStatut === "envoi"}
        >
          <StickyNote className="size-4" />
          {d.note_id !== null
            ? "Mettre à jour la note du deal"
            : "Ajouter aux notes du deal"}
        </Button>
        <Button type="button" variant="outline" onClick={copier}>
          <Copy className="size-4" />
          Copier le compte rendu
        </Button>
        <span
          role="status"
          className={`text-sm ${noteStatut === "echec" ? "text-destructive" : "text-muted-foreground"}`}
        >
          {noteStatut === "envoi" && "Envoi de la note…"}
          {noteStatut === "cree" && "Compte rendu ajouté aux notes du deal."}
          {noteStatut === "maj" && "Note du deal mise à jour."}
          {noteStatut === "echec" &&
            "Impossible d'ajouter la note au deal. Réessaie."}
        </span>
        <span role="status" className="text-sm text-muted-foreground">
          {copie === "ok" && "Compte rendu copié."}
          {copie === "echec" &&
            "Impossible de copier : autorise le presse-papiers du navigateur."}
        </span>
      </div>
    </div>
  );
};

const BlocQuestion = ({
  q,
  index,
  children,
}: {
  q: Question;
  index: number;
  children: React.ReactNode;
}) => {
  const id = useId();
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="flex size-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
          {index + 1}
        </span>
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {q.etape}
        </span>
      </div>
      <h4 id={id} className="text-base font-semibold">
        {q.question}
      </h4>
      <p className="text-sm text-muted-foreground">
        <span className="font-medium">À écouter :</span> {q.ecouter.join(" · ")}
      </p>
      <p className="text-sm text-muted-foreground">
        <span className="font-medium">Pour relancer :</span> {q.relance}
      </p>
      {children}
    </section>
  );
};

const ChampNombre = ({
  id,
  label,
  valeur,
  onChange,
}: {
  id: string;
  label: string;
  valeur: number | null;
  onChange: (valeur: string) => void;
}) => (
  <div className="flex flex-col gap-1">
    <Label htmlFor={id} className="text-xs text-muted-foreground">
      {label}
    </Label>
    <Input
      id={id}
      type="number"
      min={0}
      inputMode="decimal"
      value={valeur ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);
